import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { industries as industriesData } from '@/mocks/homeData';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';

export default function Industries() {
  const [search, setSearch] = useState('');
  const [activeSector, setActiveSector] = useState('all');

  const sectors = ['all', 'construction', 'agriculture', 'manufacturing', 'energy', 'healthcare', 'technology'];

  const filtered = industriesData.filter((ind) => {
    const matchSearch = !search || ind.title.toLowerCase().includes(search.toLowerCase()) || ind.description.toLowerCase().includes(search.toLowerCase());
    const matchSector = activeSector === 'all' || ind.id === activeSector;
    return matchSearch && matchSector;
  });

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-[80px]">
        {/* Header */}
        <div className="w-full px-6 md:px-10 lg:px-16 py-14 md:py-20 bg-background-100">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4"
          >
            Sectors
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-6xl font-heading font-bold text-foreground-950 mb-4"
          >
            Industries We Serve
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-foreground-600 max-w-2xl text-sm md:text-base leading-relaxed"
          >
            From construction to healthcare, we deliver specialized trade and procurement solutions across East Africa&apos;s key economic sectors.
          </motion.p>
        </div>

        <div className="w-full px-6 md:px-10 lg:px-16">
          {/* Search + Filter Bar */}
          <div className="py-6 md:py-8">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
              <div className="relative flex-1 max-w-md">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search industries..."
                  className="w-full pl-11 pr-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-full focus:outline-none focus:border-accent-400 transition-colors text-foreground-800"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {sectors.map((sector) => (
                  <button
                    key={sector}
                    onClick={() => setActiveSector(sector)}
                    className={`px-4 py-2 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeSector === sector
                        ? 'bg-accent-500 text-primary-900'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {sector === 'all' ? 'All Sectors' : sector.charAt(0).toUpperCase() + sector.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-sm text-foreground-500 mt-3">
              Showing <strong className="text-foreground-800">{filtered.length}</strong> of <strong className="text-foreground-800">{industriesData.length}</strong> industries
            </p>
          </div>

          {/* Industry Cards */}
          <div className="pb-14 md:pb-20">
            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                  <i className="ri-building-2-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-700 mb-2">No industries found</h3>
                <p className="text-sm text-foreground-500">Try adjusting your search criteria</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filtered.map((ind, idx) => (
                  <motion.div
                    key={ind.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: idx * 0.08 }}
                    className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:border-accent-200/50 transition-all duration-400"
                  >
                    <div className="relative h-52 overflow-hidden">
                      <img src={ind.image} alt={ind.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/50 via-transparent to-transparent"></div>
                      <div className="absolute top-4 left-4 w-11 h-11 flex items-center justify-center rounded-xl bg-white/90 backdrop-blur-sm text-primary-600">
                        <i className={`${ind.icon} text-lg`}></i>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">{ind.title}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed mb-4">{ind.description}</p>
                      <Link
                        to={`/contact?interest=${ind.id}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-accent-500 text-primary-900 rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        Inquire Now <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Stats Bar */}
        <div className="w-full px-6 md:px-10 lg:px-16 py-14 bg-primary-900">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
            {[
              { number: '500+', label: 'Verified Suppliers' },
              { number: '30+', label: 'Countries Served' },
              { number: '10,000+', label: 'Successful Shipments' },
              { number: '98%', label: 'Client Satisfaction' },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="text-center"
              >
                <motion.div
                  initial={{ scale: 0.6 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.12 + 0.2, type: 'spring', stiffness: 120 }}
                  className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-1"
                >
                  {stat.number}
                </motion.div>
                <div className="text-xs md:text-sm text-white/70">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="w-full px-6 md:px-10 lg:px-16 py-14 md:py-20 text-center bg-background-100"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3"
          >
            Ready to Source with Confidence?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-sm text-foreground-500 max-w-xl mx-auto mb-6"
          >
            Tell us about your industry requirements, and our team of sector specialists will create a tailored procurement strategy for your business.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Start a Conversation <i className="ri-arrow-right-line"></i>
            </Link>
          </motion.div>
        </motion.div>
      </main>
      <Footer />
    </>
  );
}